import React from 'react';
import { AudioInputProps } from '../../AudioInput.types';
import { AudioInputController } from '../../useAudioInputController';
import BootstrapDropzone from './BootstrapDropzone';
import BootstrapRecordingPanel from './BootstrapRecordingPanel';
import { RecordedAudioPlayer } from '../../../ai-voices/voice-cloning';

interface BootstrapAudioInputViewProps {
    props: AudioInputProps;
    controller: AudioInputController;
}

// Dropzone/recorder hide once a file exists — only the preview + discard button show.
export default function BootstrapAudioInputView({ props, controller }: BootstrapAudioInputViewProps) {
    const { recording = {}, validation = {}, multiple = false, maxFiles = 1, renderUploadedPreview, renderError, className, compact = false } = props;
    const { allowRecording = true, micDeviceSelector = false, maxRecordingDurationSec = 30 } = recording;
    const minRecordingDurationSec = validation.minDurationSec ?? 0;

    const {
        files, isRecording, isStartingRecording, isRecordingPanelOpen, countdown, recordingDuration, error,
        micDevices, selectedMicDeviceId, setSelectedMicDeviceId,
        fileInputRef, openFilePicker, openRecorder, closeRecorder, handleFileInputChange, handleDrop,
        startCountdownThenRecord, stopRecording, clearFile, reportDuration,
    } = controller;

    const sizeHintMb = validation.maxSizeBytes ? Math.round(validation.maxSizeBytes / (1024 * 1024)) : 10;
    const durationHint = validation.maxDurationSec
        ? validation.maxDurationSec >= 60
            ? `${Math.round(validation.maxDurationSec / 60)} min`
            : `${Math.round(validation.maxDurationSec)} sec`
        : null;
    const showPanel = isRecordingPanelOpen || isRecording || countdown !== null;
    const canAddMore = multiple ? files.length < maxFiles : files.length === 0;
    const acceptAttr = (validation.allowedMimePrefixes ?? ['audio/'])
        .map(prefix => `${prefix}*`)
        .join(',');

    return (
        <div className={className}>
            {showPanel ? (
                <BootstrapRecordingPanel
                    compact={compact}
                    onBack={closeRecorder}
                    isRecording={isRecording}
                    isStartingRecording={isStartingRecording}
                    countdown={countdown}
                    recordingDuration={recordingDuration}
                    maxRecordingDurationSec={maxRecordingDurationSec}
                    minRecordingDurationSec={minRecordingDurationSec}
                    showMicPicker={micDeviceSelector}
                    micDevices={micDevices}
                    selectedMicDeviceId={selectedMicDeviceId}
                    onSelectMicDevice={setSelectedMicDeviceId}
                    onStartCountdown={startCountdownThenRecord}
                    onStop={stopRecording}
                />
            ) : canAddMore ? (
                <BootstrapDropzone
                    compact={compact}
                    allowRecording={allowRecording}
                    hint={`Supports: MP3, WAV, M4A, OGG (Max ${sizeHintMb}MB${durationHint ? ` or ${durationHint}` : ''})`}
                    onClick={openFilePicker}
                    onDragOver={e => e.preventDefault()}
                    onDrop={handleDrop}
                    onRecordClick={openRecorder}
                />
            ) : null}

            <input
                ref={fileInputRef}
                type="file"
                accept={acceptAttr}
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
            />

            {files.map((f, i) => (
                <div key={i} className="mt-3">
                    {renderUploadedPreview
                        ? renderUploadedPreview(f, { clear: () => clearFile(multiple ? f : undefined) })
                        : (f.previewUrl && (
                            <RecordedAudioPlayer
                                audioUrl={f.previewUrl}
                                fileName={f.file.name}
                                onRemove={() => clearFile(multiple ? f : undefined)}
                            />
                        ))}
                </div>
            ))}

            {error && (
                renderError
                    ? renderError(error)
                    : <div className="mt-2" style={{ fontSize: 13, color: 'var(--bs-danger)', fontWeight: 500 }}>{error.message}</div>
            )}

            {/* Hidden audio elements just to read each file's duration once metadata loads. */}
            {files.filter(f => f.durationSec == null).map((f, i) => (
                <audio
                    key={i}
                    src={f.previewUrl}
                    style={{ display: 'none' }}
                    onLoadedMetadata={e => reportDuration(f, (e.target as HTMLAudioElement).duration)}
                />
            ))}
        </div>
    );
}
